import { Printer } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react'
import entete from "../../../assets/img/entete_pool.png"
import { Checkbox, Image } from 'antd';
import BarreTop from '../../../components/docs/BarreTop';
import { DocumentFooter } from '../../../components/docs/DocumentFooter';
import { frenchDate, numberToWordsFrench } from '../../../variables/functions';
import { getCompagnieLogo } from '../../../hooks/useFilesHooks';
import { useReactToPrint } from 'react-to-print';
import axiosInstance from '../../../variables/api/axiosInstance';

// Couleurs choisies pour rester lisibles à l'impression noir & blanc :
// le navy (entêtes) vire en gris foncé, les teintes claires en gris très clair,
// et les montants utilisent des tons foncés (et non pastel) qui gardent du contraste.
const NAVY = '#242166';
const INK = '#1f2937';
const SUB = '#5b5b6b';
const BORDER = '#cbd0dd';
const TINT = '#f3f5fb';   // fond très clair des encarts (texte)
const ZEBRA = '#f6f7fb';
const GREEN = '#15803d';  // foncé → lisible en N&B
const RED = '#b91c1c';
const ORANGE = '#c2410c';

const PriseEnCharge = (props) => {
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    // L'organisme embarqué dans le contrat peut être une version historisée
    // (active_data=false) → on récupère la version ACTIVE par code.
    const [organisme, setOrganisme] = useState(props?.sinistre?.contrat?.organisme || {});
    const printRef = useRef(null);
    // Impression sur PAPIER À EN-TÊTE : masque l'en-tête (logos + barre) et le pied de page
    // logiciels, tout en RÉSERVANT leur hauteur pour que le contenu reste à la même position
    // et s'aligne sur l'en-tête/pied pré-imprimés. Défaut : affichés (comportement inchangé).
    const [afficherEnteteEtPied, setAfficherEnteteEtPied] = useState(
        props?.afficherEnteteEtPied !== false
    );

    useEffect(() => {
        const code = props?.sinistre?.contrat?.organisme?.code;
        if (!code) return;
        axiosInstance
            .get(`/api/organisme/code/${code}`)
            .then(({ data }) => { if (data) setOrganisme(data); })
            .catch((err) => console.log(err));
    }, [props?.sinistre?.contrat?.organisme?.code]);

    const orgTelephone = organisme?.telephone || organisme?.telephone1 || organisme?.telephone2 || "";

    const isBon = !!props?.priseEnCharge?.bonReparation;
    const T = {
        docTitle: isBon ? "BON DE RÉPARATION" : "ATTESTATION DE PRISE EN CHARGE",
        numLabel: isBon ? "Numéro du Bon de réparation" : "Numéro de Prise en Charge",
        etabLabel: isBon ? "Garage / Atelier" : "Établissement / Centre de soins / Prestataire",
        detailsTitle: isBon ? "DÉTAILS DE LA RÉPARATION" : "DÉTAILS DE L'ACTE MÉDICAL",
        natureLabel: isBon ? "Type de réparation" : "Nature de l'Acte",
        montantLabel: isBon ? "Coût de la réparation" : "Montant Total",
        chargesTitle: isBon ? "DÉTAIL DES TRAVAUX / PIÈCES" : "DÉTAIL DES CHARGES",
        acteCol: isBon ? "Travaux / Pièce" : "Acte",
        validiteMot: isBon ? "bon de réparation" : "prise en charge",
        beneficiaireLabel: isBon ? "Bénéficiaire" : "Victime",
    };

    useEffect(() => {
        if (props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(props?.sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    var blob = new Blob([imageBytes], { type: "image/png" });
                    var imageUrl = URL.createObjectURL(blob);
                    setLogoPreviewUrl(imageUrl);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    }, []);

    const handleGeneratePDF = async () => {
        const element = printRef.current;
        const html2pdf = (await import('html2pdf.js')).default;

        const opt = {
            margin: [0, 0, 0, 0],
            filename: `${isBon ? 'Bon_de_reparation' : 'Prise_en_charge'}_${props?.priseEnCharge.numeroPriseEnCharge}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'legacy'], before: '.page-break' }
        };

        html2pdf().set(opt).from(element).toPdf().get('pdf').then(function (pdf) {
            window.open(pdf.output('bloburl'), '_blank');
        });
    };

    // eslint-disable-next-line no-unused-vars
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: `@page { size: A4 portrait; margin: 0; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`,
        documentTitle: T.docTitle
    });

    const calculerTotaux = () => ({
        totalReclame: props?.priseEnCharge?.charge?.reduce((sum, c) => sum + c.montantReclame, 0),
        totalRetenue: props?.priseEnCharge?.charge?.reduce((sum, c) => sum + c.montantRetenue, 0),
        totalExclu: props?.priseEnCharge?.charge?.reduce((sum, c) => sum + c.montantExclu, 0),
    });

    const totaux = calculerTotaux();
    const genreLabel = props?.priseEnCharge?.victime?.personne?.genre === 'MASCULIN' ? 'M.' : 'Mme.';
    const beneficiaireNom = `${props?.priseEnCharge?.victime?.personne?.prenom || ""} ${props?.priseEnCharge?.victime?.personne?.nom || ""}`.trim();
    const fmtXOF = (v) => `${Number(v || 0).toLocaleString('fr-FR')} F CFA`;

    // Montant de la réparation (bon) : coût saisi, à défaut le total réclamé de l'annexe.
    const montantReparation = Number(props?.priseEnCharge?.montant) || Number(totaux.totalReclame) || 0;

    // Nom COMPLET de la branche de l'engin (au lieu du code de catégorie « MOTO »).
    const categorieEngin = props?.sinistre?.engin?.categorie;
    const brancheNom = categorieEngin?.branche?.nom
        || categorieEngin?.brancheNom
        || categorieEngin?.nom
        || categorieEngin?.codeCategorie
        || "";

    // Documents à réclamer (bloc « Vous voudrez bien nous adresser : … ») : sélection enregistrée
    // sur la PEC (snapshot d'intitulés). À défaut → les deux documents historiques.
    const DOCS_DEFAUT = [
        "Votre facture (lue, approuvée et signée par le patient et le médecin-chef) et la présente prise en charge",
        "Un rapport médical sous pli confidentiel à l'attention de notre médecin conseil",
    ];
    const docsAReclamer = (() => {
        try {
            const parsed = props?.priseEnCharge?.documentsAReclamerJson
                ? JSON.parse(props.priseEnCharge.documentsAReclamerJson) : null;
            const lignes = Array.isArray(parsed?.lignes)
                ? parsed.lignes.map((s) => String(s || "").trim()).filter(Boolean) : null;
            return lignes && lignes.length ? lignes : DOCS_DEFAUT;
        } catch (_) {
            return DOCS_DEFAUT;
        }
    })();

    // Durée de validité en toutes lettres : « 1M » → « 1 mois », « 2A » → « 2 ans », etc.
    const formatDuree = (d) => {
        if (!d) return d;
        const s = String(d).trim();
        let m;
        if ((m = s.match(/^(\d+)\s*M(OIS)?$/i))) return `${m[1]} mois`;
        if ((m = s.match(/^(\d+)\s*A(N|NS|NN[EÉ]E?S?)?$/i))) return `${m[1]} an${Number(m[1]) > 1 ? 's' : ''}`;
        if ((m = s.match(/^(\d+)\s*J(OURS?)?$/i))) return `${m[1]} jour${Number(m[1]) > 1 ? 's' : ''}`;
        return s;
    };

    // ── Styles réutilisables ───────────────────────────────────────────
    const box = { marginBottom: '6px', border: `1px solid ${BORDER}`, borderRadius: '4px', overflow: 'hidden' };
    const sectionHead = {
        backgroundColor: NAVY, color: '#ffffff', padding: '5px 12px', fontWeight: 'bold',
        fontSize: '10.5px', letterSpacing: '0.4px', textTransform: 'uppercase',
    };
    const fieldLabel = { fontWeight: 'bold', color: SUB, marginBottom: '1px' };
    const fieldValue = { color: INK };
    const noteBox = (accent) => ({
        marginBottom: '6px', border: `1px solid ${BORDER}`, borderLeft: `4px solid ${accent}`,
        backgroundColor: TINT, padding: '8px 10px', fontSize: '10.5px', lineHeight: '1.4', color: INK,
    });

    // Page A4 : padding INTERNE (box-sizing). Hauteur bornée à 296mm (< 297mm) :
    // avec minHeight = 297mm exactement, les arrondis de rendu html2canvas faisaient
    // déborder la page 1 de quelques pixels sur la feuille 2 (vide), et le saut de
    // page repoussait la page 2 sur une 3e feuille. 296mm + overflow hidden
    // garantissent 1 bloc = 1 feuille → le document sort en 2 pages.
    const pageStyle = {
        width: '210mm', height: '296mm', maxHeight: '296mm', overflow: 'hidden',
        boxSizing: 'border-box', padding: '9mm 13mm',
        display: 'flex', flexDirection: 'column', backgroundColor: 'white',
    };

    const HeaderComponent = () => (
        // Papier à en-tête : on masque les logos + la barre mais on RÉSERVE leur hauteur
        // (le contenu garde sa position, l'en-tête pré-imprimé occupe cette zone).
        afficherEnteteEtPied ? (
            <div style={{ flexShrink: 0, marginBottom: '6px' }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {organisme?.typeOrganisme !== "POOL" && (
                        <Image src={logoPreviewUrl} preview={false} style={{ width: "100%", height: "62px" }} />
                    )}
                    <Image src={entete} preview={false} style={{ width: "100%", height: "62px" }} />
                </div>
                <BarreTop />
            </div>
        ) : (
            <div style={{ flexShrink: 0, height: '72px', marginBottom: '6px' }} />
        )
    );

    const FooterComponent = () => (
        // Papier à en-tête : pied logiciel masqué mais hauteur réservée (marginTop auto → le
        // contenu reste poussé vers le haut, le pied pré-imprimé occupe le bas de la feuille).
        afficherEnteteEtPied ? (
            <div style={{ marginTop: 'auto', paddingTop: '8px' }}>
                <DocumentFooter />
            </div>
        ) : (
            <div style={{ marginTop: 'auto', paddingTop: '8px', height: '48px' }} />
        )
    );

    const InfoCell = ({ label, value, full }) => (
        <div style={full ? { gridColumn: '1 / -1' } : undefined}>
            <div style={fieldLabel}>{label}</div>
            <div style={fieldValue}>{value}</div>
        </div>
    );

    return (
        <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <div className="doc-toolbar" style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                    onClick={handleGeneratePDF}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
                        backgroundColor: NAVY, color: 'white', border: 'none', borderRadius: '4px',
                        cursor: 'pointer', fontSize: '14px', fontWeight: '500'
                    }}
                >
                    <Printer size={18} /> Imprimer
                </button>
                <Checkbox
                    checked={!afficherEnteteEtPied}
                    onChange={(e) => setAfficherEnteteEtPied(!e.target.checked)}
                    style={{ marginLeft: '4px' }}
                >
                    Imprimer sur papier à en-tête
                </Checkbox>
            </div>

            <div ref={printRef} style={{
                backgroundColor: 'white', width: '210mm', margin: '0 auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)', fontFamily: '"Roboto", Arial, sans-serif',
                lineHeight: '1.4', color: INK
            }}>
                {/* PAGE 1 */}
                <div className="page" style={pageStyle}>
                    <HeaderComponent />

                    <div style={{ flex: '1' }}>
                        <div style={{ textAlign: 'center', marginBottom: '8px', borderTop: `2px solid ${NAVY}`, borderBottom: `2px solid ${NAVY}`, padding: '6px 0' }}>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: NAVY, letterSpacing: '1px' }}>
                                {T.docTitle}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '6px', fontSize: '11px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ fontWeight: 'bold', color: NAVY }}>{T.numLabel} :</div>
                                <div style={{ fontWeight: 'bold', color: INK }}>{props?.priseEnCharge.numeroPriseEnCharge}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <div style={{ fontWeight: 'bold', color: NAVY }}>Date d'émission :</div>
                                <div style={{ color: INK }}>{frenchDate(props?.priseEnCharge?.createdAt)}</div>
                            </div>
                        </div>

                        {isBon ? (
                            <>
                                {/* Bon de réparation : bloc UNIQUE « Informations sur la police »
                                    (souscripteur + police fusionnés, immatriculation ramenée ici). */}
                                <div style={box}>
                                    <div style={sectionHead}>Informations sur la police</div>
                                    <div style={{ padding: '8px 10px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', fontSize: '11px' }}>
                                            <InfoCell label="Numéro Assuré" value={props?.sinistre?.contrat?.souscripteur?.numeroAssureP || props?.sinistre?.contrat?.souscripteur?.numeroAssureM} />
                                            <InfoCell label="Nom Complet" value={`${props?.sinistre?.contrat?.souscripteur?.nom || ""} ${props?.sinistre?.contrat?.souscripteur?.prenom || ""}`} />
                                            <InfoCell label="Téléphone" value={props?.sinistre?.contrat?.souscripteur?.contact?.length ? props?.sinistre?.contrat?.souscripteur?.contact[0] : ""} />
                                            <InfoCell label="Adresse" value={props?.sinistre?.contrat?.souscripteur?.adresse || ""} />
                                            <InfoCell label="Numéro de Police" value={props?.sinistre?.contrat?.numeroPoliceP} />
                                            <InfoCell label="Catégorie" value={brancheNom} />
                                            <InfoCell label="N° d'Immatriculation" value={props?.sinistre?.engin?.immatriculation} />
                                            <InfoCell label="Date d'Effet" value={props?.sinistre?.contrat?.dateEffet ? new Date(props?.sinistre?.contrat?.dateEffet).toLocaleDateString('fr-FR') : ""} />
                                            <InfoCell label="Date d'Échéance" value={props?.sinistre?.contrat?.dateEcheance ? new Date(props?.sinistre?.contrat?.dateEcheance).toLocaleDateString('fr-FR') : ""} />
                                        </div>
                                    </div>
                                </div>

                                <div style={box}>
                                    <div style={sectionHead}>Informations sur le sinistre</div>
                                    <div style={{ padding: '8px 10px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '11px' }}>
                                            <InfoCell label="Numéro du Sinistre" value={props?.sinistre?.numSinistre || props?.sinistre?.numeroSinistreM} />
                                            <InfoCell label="Date de Survenance" value={props?.sinistre?.dateSinistre ? new Date(props?.sinistre.dateSinistre).toLocaleDateString('fr-FR') : ""} />
                                            <InfoCell label="Date de Déclaration" value={props?.sinistre?.dateDeclaration ? new Date(props?.sinistre.dateDeclaration).toLocaleDateString('fr-FR') : ""} />
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                                    <div style={box}>
                                        <div style={sectionHead}>Informations du souscripteur</div>
                                        <div style={{ padding: '8px 10px' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                <InfoCell label="Numéro Assuré" value={props?.sinistre?.contrat?.souscripteur?.numeroAssureP || props?.sinistre?.contrat?.souscripteur?.numeroAssureM} />
                                                <InfoCell label="Nom Complet" value={`${props?.sinistre?.contrat?.souscripteur?.nom || ""} ${props?.sinistre?.contrat?.souscripteur?.prenom || ""}`} />
                                                <InfoCell label="Téléphone" value={props?.sinistre?.contrat?.souscripteur?.contact?.length ? props?.sinistre?.contrat?.souscripteur?.contact[0] : ""} />
                                                <InfoCell label="Adresse" value={props?.sinistre?.contrat?.souscripteur?.adresse || ""} full />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={box}>
                                        <div style={sectionHead}>Informations de la police</div>
                                        <div style={{ padding: '8px 10px' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                <InfoCell label="Numéro de Police" value={props?.sinistre?.contrat?.numeroPoliceP} />
                                                <InfoCell label="Catégorie d'Engin" value={props?.sinistre?.engin?.categorie?.codeCategorie} />
                                                <InfoCell label="Date d'Effet" value={props?.sinistre?.contrat?.dateEffet ? new Date(props?.sinistre?.contrat?.dateEffet).toLocaleDateString('fr-FR') : ""} />
                                                <InfoCell label="Date d'Échéance" value={props?.sinistre?.contrat?.dateEcheance ? new Date(props?.sinistre?.contrat?.dateEcheance).toLocaleDateString('fr-FR') : ""} />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div style={box}>
                                    <div style={sectionHead}>Informations du sinistre</div>
                                    <div style={{ padding: '8px 10px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                                            <InfoCell label="Numéro du Sinistre" value={props?.sinistre?.numSinistre || props?.sinistre?.numeroSinistreM} />
                                            <InfoCell label="Numéro d'Immatriculation" value={props?.sinistre?.engin?.immatriculation} />
                                            <InfoCell label="Date de Survenance" value={props?.sinistre?.dateSinistre ? new Date(props?.sinistre.dateSinistre).toLocaleDateString('fr-FR') : ""} />
                                            <InfoCell label="Date de Déclaration" value={props?.sinistre?.dateDeclaration ? new Date(props?.sinistre.dateDeclaration).toLocaleDateString('fr-FR') : ""} />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Texte juridique / d'autorisation */}
                        {isBon ? (
                            <div style={noteBox(NAVY)}>
                                <div style={{ marginBottom: '6px' }}>
                                    <strong>Nous soussignés, {organisme?.raisonSociale}</strong>, sis à {organisme?.siegeSocial}
                                    <strong> Tel : {orgTelephone}</strong>, autorisons
                                    <strong style={{ backgroundColor: '#fde68a', padding: '0 3px', borderRadius: '2px' }}> {props?.priseEnCharge?.etablissement?.nom}</strong> à effectuer les travaux de réparation ci-après désignés sur le véhicule immatriculé
                                    <strong> {props?.sinistre?.engin?.immatriculation}</strong>{beneficiaireNom ? <> (bénéficiaire : <strong>{beneficiaireNom}</strong>)</> : null}.
                                </div>
                                <div>
                                    Ces travaux sont autorisés. La facture définitive, dûment datée, signée et acquittée,
                                    devra nous être adressée accompagnée du présent bon de réparation.
                                </div>
                            </div>
                        ) : (
                            <div style={noteBox('#d97706')}>
                                <div style={{ marginBottom: '6px' }}>
                                    <strong>Nous soussignés, {organisme?.raisonSociale}</strong>, sise {organisme?.siegeSocial}
                                    <strong> Tel : {orgTelephone}</strong>, attestons que :
                                </div>
                                <div style={{ marginBottom: '6px' }}>
                                    <strong>{genreLabel} {beneficiaireNom}</strong> est {props?.priseEnCharge?.victime?.personne?.genre === "MASCULIN" ? "pris" : "prise"} en charge par les compagnies membres du Pool,
                                    <em> conformément aux dispositions de <u>l’article 258 du code CIMA</u> qui stipule : « Les frais de toute nature peuvent être, soit remboursés à la victime sur présentation des pièces justificatives, soit pris en charge directement par l’assureur du véhicule ayant causé l’accident.</em>
                                </div>
                                <div>
                                    <em><strong><u>Toutefois, leurs coûts ne sauraient excéder deux fois le tarif le plus élevé des hôpitaux publics du pays de l’accident</u></strong> et en cas d’évacuation sanitaire justifiée par expertise, une fois le tarif le plus élevé des hôpitaux publics du pays d’accueil …»</em>
                                </div>
                            </div>
                        )}

                        {isBon ? (
                            /* Bon de réparation : on remplace le bloc « Détails de la réparation »
                               par le MONTANT DE LA RÉPARATION (en chiffres et en toutes lettres). */
                            <div style={box}>
                                <div style={sectionHead}>Montant de la réparation</div>
                                <div style={{ padding: '10px 12px', fontSize: '11.5px' }}>
                                    <div style={{ marginBottom: '6px' }}>
                                        <span style={{ fontWeight: 'bold', color: SUB }}>En chiffres : </span>
                                        <strong style={{ color: GREEN, fontSize: '13px' }}>{fmtXOF(montantReparation)}</strong>
                                    </div>
                                    <div>
                                        <span style={{ fontWeight: 'bold', color: SUB }}>En lettres : </span>
                                        <strong style={{ color: INK, textTransform: 'uppercase' }}>
                                            {numberToWordsFrench(montantReparation)} FRANCS CFA
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={box}>
                                <div style={sectionHead}>{T.detailsTitle}</div>
                                <div style={{ padding: '8px 10px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                                        <InfoCell label={T.beneficiaireLabel} value={beneficiaireNom || "-"} />
                                        <InfoCell label={T.etabLabel} value={props?.priseEnCharge?.etablissement?.nom} />
                                        <InfoCell label={T.natureLabel} value={`${props?.priseEnCharge?.natureActe || ""} ${props?.priseEnCharge?.codification ? `(${props?.priseEnCharge?.codification})` : ""}`} />
                                        <InfoCell label={T.montantLabel} value={<strong style={{ color: GREEN, fontSize: '12.5px' }}>{fmtXOF(props?.priseEnCharge?.montant)}</strong>} />
                                        <InfoCell label="Durée de Validité" value={formatDuree(props?.priseEnCharge?.dureeValidite)} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* N.B. */}
                        {isBon ? (
                            <div style={noteBox(GREEN)}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Recommandations importantes :</div>
                                <ul style={{ marginLeft: '20px', marginTop: '4px', marginBottom: 0 }}>
                                    <li>Tout dépassement du montant retenu nécessite une entente préalable avec l'assureur.</li>
                                </ul>
                            </div>
                        ) : (
                            <div style={noteBox(GREEN)}>
                                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>N.B. :</div>
                                <div style={{ marginBottom: '6px' }}>Vous voudrez bien nous adresser :</div>
                                <ul style={{ marginLeft: '20px', marginTop: '4px', marginBottom: '8px' }}>
                                    {docsAReclamer.map((ligne, i) => (
                                        <li key={i}>{ligne}{i < docsAReclamer.length - 1 ? ' ;' : '.'}</li>
                                    ))}
                                </ul>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Recommandations très importantes :</div>
                                <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                                    <li>Une hospitalisation excédant la durée probable mentionnée plus haut nécessite une entente préalable avec l'assureur.</li>
                                    <li>En cas de dépassement du montant, une autorisation doit être demandée à l'assureur.</li>
                                </ul>
                            </div>
                        )}

                        <div style={{ ...noteBox(NAVY), marginBottom: '0' }}>
                            <strong>Validité :</strong> Le présent {T.validiteMot} est valable pour une période de <strong>{formatDuree(props?.priseEnCharge?.dureeValidite)}</strong> à compter de sa date d'émission.
                        </div>
                    </div>

                    {/* Signature du Directeur — sur la PREMIÈRE page (le bon / la prise en charge
                        est signé ici). La page 2 n'est qu'une ANNEXE (détail des charges + résumé
                        financier). flexShrink:0 : garde sa place ; le contenu flex:1 la pousse en
                        bas de la feuille, au-dessus du pied de page. */}
                    <div style={{ flexShrink: 0, marginTop: '14px', display: 'flex', justifyContent: 'flex-end', fontSize: '11px' }}>
                        <div style={{ textAlign: 'left' }}>
                            <div style={{ marginBottom: '48px', fontWeight: 'bold' }}>
                                Fait à Lomé, le {frenchDate(props?.priseEnCharge?.createdAt)}
                            </div>
                            <div style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                {organisme?.responsable}
                            </div>
                            <div style={{ marginTop: '4px', fontWeight: 'bold' }}>Directeur</div>
                        </div>
                    </div>

                    <FooterComponent />
                </div>

                {/* SAUT DE PAGE */}
                <div className="page-break"></div>

                {/* PAGE 2 */}
                <div className="page" style={pageStyle}>
                    <HeaderComponent />

                    <div style={{ flex: '1' }}>
                        <div style={box}>
                            <div style={sectionHead}>{T.chargesTitle}</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#eef0f7', borderBottom: `1.5px solid ${NAVY}` }}>
                                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold', color: NAVY }}>{T.acteCol}</th>
                                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: NAVY }}>Montant Réclamé</th>
                                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: NAVY }}>Retenu</th>
                                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: NAVY }}>Exclu</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {props?.priseEnCharge?.charge?.map((item, index) => (
                                        <tr key={item?.codeId || index} style={{ borderBottom: `1px solid ${BORDER}`, backgroundColor: index % 2 === 0 ? '#ffffff' : ZEBRA }}>
                                            <td style={{ padding: '8px', color: INK }}>
                                                <div style={{ fontWeight: '500' }}>{item.acte}</div>
                                                <div style={{ fontSize: '9px', color: SUB }}>{item?.codificationActe}</div>
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'right', color: GREEN, fontWeight: '500' }}>{fmtXOF(item?.montantReclame)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right', color: RED, fontWeight: '500' }}>{fmtXOF(item?.montantRetenue)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right', color: ORANGE, fontWeight: '500' }}>{fmtXOF(item?.montantExclu)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ ...box, border: `1.5px solid ${NAVY}` }}>
                            <div style={sectionHead}>Résumé financier</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0' }}>
                                {[
                                    { label: 'Total Réclamé', value: totaux.totalReclame, color: GREEN },
                                    { label: 'Retenues', value: totaux.totalRetenue, color: RED },
                                    { label: 'Exclusions', value: totaux.totalExclu, color: ORANGE },
                                ].map((c, i) => (
                                    <div key={c.label} style={{ padding: '8px 10px', borderLeft: `4px solid ${c.color}`, borderRight: i < 2 ? `1px solid ${BORDER}` : 'none', textAlign: 'center' }}>
                                        <div style={{ color: SUB, marginBottom: '4px', fontSize: '10.5px' }}>{c.label}</div>
                                        <div style={{ fontWeight: 'bold', color: c.color, fontSize: '13px' }}>{fmtXOF(c.value)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <FooterComponent />
                </div>
            </div>

            <style>{`
                .page-break { page-break-before: always; break-before: page; height: 0; }
                @media print {
                    body { margin: 0; padding: 0; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .doc-toolbar { display: none !important; }
                }
            `}</style>
        </div>
    );
}

PriseEnCharge.propTypes = {
    priseEnCharge: PropTypes.object.isRequired,
    sinistre: PropTypes.object.isRequired,
    /** false → impression sans en-tête/pied logiciels (papier à en-tête). Défaut : true. */
    afficherEnteteEtPied: PropTypes.bool,
}

export default PriseEnCharge;
